import ReactGA from 'react-ga4';
import { analytics } from './analytics';

const loadConsentAwareMonitoring = (() => {
  let cached: Promise<typeof import('./consentAwareMonitoring')> | null = null;
  return () => {
    cached ??= import('./consentAwareMonitoring');
    return cached;
  };
})();

// Google Analytics Measurement ID
const GA_MEASUREMENT_ID = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || 'G-9HT32G0P63';

interface ConsentAwareAnalyticsConfig {
  debug?: boolean;
  testMode?: boolean;
  respectConsent?: boolean;
}

interface ConsentPreferences {
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

/**
 * Consent-aware analytics service that respects GDPR consent preferences
 */
class ConsentAwareAnalyticsService {
  private isInitialized = false;
  private config: ConsentAwareAnalyticsConfig;
  private consentPreferences: ConsentPreferences | null = null;
  private pendingEvents: Array<() => void> = [];

  constructor(config: ConsentAwareAnalyticsConfig = {}) {
    this.config = {
      respectConsent: true,
      ...config
    };
  }

  /**
   * Initialize Google Analytics with consent preferences
   */
  async initialize(consentPreferences?: ConsentPreferences): Promise<void> {
    if (this.isInitialized) {
      console.warn('Consent-aware Google Analytics already initialized');
      return;
    }

    try {
      // Load consent preferences if not provided
      if (!consentPreferences) {
        consentPreferences = await this.loadConsentPreferences();
      }

      this.consentPreferences = consentPreferences;

      // Only initialize if analytics consent is given or consent checking is disabled
      if (!this.config.respectConsent || consentPreferences.analytics) {
        ReactGA.initialize(GA_MEASUREMENT_ID, {
          testMode: this.config.testMode || false,
          gtagOptions: {
            // Enhanced privacy settings for GDPR compliance
            anonymize_ip: true,
            cookie_flags: 'SameSite=None;Secure',
            // Respect consent preferences
            ad_storage: consentPreferences.marketing ? 'granted' : 'denied',
            analytics_storage: consentPreferences.analytics ? 'granted' : 'denied',
            functionality_storage: consentPreferences.functional ? 'granted' : 'denied',
            personalization_storage: consentPreferences.marketing ? 'granted' : 'denied',
            security_storage: 'granted', // Always granted for security
          },
        });

        this.isInitialized = true;
        console.log('Consent-aware Google Analytics initialized successfully');

        // Process any pending events
        this.processPendingEvents();
      } else {
        console.log('Google Analytics not initialized - analytics consent not given');
      }
    } catch (error) {
      console.error('Failed to initialize consent-aware Google Analytics:', error);
    }
  }

  /**
   * Update consent preferences and reinitialize if needed
   */
  async updateConsentPreferences(preferences: ConsentPreferences): Promise<void> {
    this.consentPreferences = preferences;

    // Update gtag consent if already initialized
    if (this.isInitialized && typeof (window as any).gtag !== 'undefined') {
      (window as any).gtag('consent', 'update', {
        ad_storage: preferences.marketing ? 'granted' : 'denied',
        analytics_storage: preferences.analytics ? 'granted' : 'denied',
        functionality_storage: preferences.functional ? 'granted' : 'denied',
        personalization_storage: preferences.marketing ? 'granted' : 'denied',
      });

      console.log('Google Analytics consent preferences updated');
    } else if (!this.isInitialized && preferences.analytics) {
      // Initialize if analytics consent was just granted
      await this.initialize(preferences);
    }

    // Store consent preferences for future sessions
    await this.storeConsentPreferences(preferences);
  }

  /**
   * Track page views with consent checking
   */
  trackPageView(path: string, title?: string, additionalData?: Record<string, any>): void {
    if (!this.canTrack('analytics')) {
      this.queueEvent(() => this.trackPageView(path, title, additionalData));
      return;
    }

    try {
      ReactGA.send({
        hitType: 'pageview',
        page: path,
        title: title || document.title,
        ...additionalData,
      });

      if (this.config.debug) {
        console.log('Consent-aware page view tracked:', { path, title, additionalData });
      }
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }

  /**
   * Track custom events with consent checking
   */
  trackEvent(action: string, category: string, label?: string, value?: number): void {
    if (!this.canTrack('analytics')) {
      this.queueEvent(() => this.trackEvent(action, category, label, value));
      return;
    }

    try {
      ReactGA.event({
        action,
        category,
        label,
        value,
      });

      if (this.config.debug) {
        console.log('Consent-aware event tracked:', { action, category, label, value });
      }
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  /**
   * Track consent-related events
   */
  trackConsentEvent(action: string, consentType: string, granted: boolean): void {
    // Always track consent events for compliance monitoring
    try {
      ReactGA.event({
        action,
        category: 'GDPR_Consent',
        label: consentType,
        value: granted ? 1 : 0
      } as any);

      // Track in monitoring system
      const actionMap: { [key: string]: 'given' | 'denied' | 'withdrawn' | 'updated' } = {
        'consent_given': 'given',
        'consent_denied': 'denied',
        'consent_withdrawn': 'withdrawn',
        'consent_updated': 'updated',
      };

      const consentTypeMap: { [key: string]: 'analytics' | 'marketing' | 'functional' } = {
        'analytics': 'analytics',
        'marketing': 'marketing',
        'functional': 'functional',
        'data_processing': 'analytics', // Map data processing to analytics
      };

      const mappedAction = actionMap[action] || 'updated';
      const mappedType = consentTypeMap[consentType] || 'analytics';

      loadConsentAwareMonitoring()
        .then(({ consentAwareMonitoring }) => {
          consentAwareMonitoring.trackConsentEvent(mappedAction, mappedType, granted);
        })
        .catch((monitoringError) => {
          if (this.config.debug) {
            console.warn('Consent monitoring unavailable:', monitoringError);
          }
        });

      if (this.config.debug) {
        console.log('Consent event tracked:', { action, consentType, granted });
      }
    } catch (error) {
      console.error('Failed to track consent event:', error);
    }
  }

  /**
   * Track marketing-related events (requires marketing consent)
   */
  trackMarketingEvent(action: string, category: string, label?: string, value?: number): void {
    if (!this.canTrack('marketing')) {
      console.log('Marketing event not tracked - marketing consent not given');
      return;
    }

    this.trackEvent(action, category, label, value);
  }

  /**
   * Track registration events with consent data
   */
  trackRegistrationEvent(
    action: string, 
    courseId: string, 
    consentData: {
      dataProcessing: boolean;
      marketing: boolean;
    },
    registrationId?: string
  ): void {
    if (!this.canTrack('analytics')) {
      this.queueEvent(() => this.trackRegistrationEvent(action, courseId, consentData, registrationId));
      return;
    }

    try {
      ReactGA.event({
        action,
        category: 'Course_Registration',
        label: courseId
      } as any);

      // Track in monitoring system
      if (registrationId) {
        loadConsentAwareMonitoring()
          .then(({ consentAwareMonitoring }) => {
            consentAwareMonitoring.trackRegistrationWithConsent(
              registrationId,
              courseId,
              {
                dataProcessing: consentData.dataProcessing,
                marketing: consentData.marketing,
                privacyPolicyAccepted: true, // Assumed if registration completed
                termsAccepted: true, // Assumed if registration completed
              }
            );
          })
          .catch((monitoringError) => {
            if (this.config.debug) {
              console.warn('Registration consent monitoring unavailable:', monitoringError);
            }
          });
      }

      // Also track consent events
      this.trackConsentEvent('consent_given', 'data_processing', consentData.dataProcessing);
      this.trackConsentEvent('consent_given', 'marketing', consentData.marketing);

      if (this.config.debug) {
        console.log('Registration event tracked with consent data:', { action, courseId, consentData });
      }
    } catch (error) {
      console.error('Failed to track registration event:', error);
    }
  }

  /**
   * Track blog-specific events
   */
  trackBlogEvent(action: string, postSlug?: string, category?: string): void {
    this.trackEvent(action, category || 'Blog', postSlug);
  }

  /**
   * Track course-related events
   */
  trackCourseEvent(action: string, courseId?: string, category?: string): void {
    this.trackEvent(action, category || 'Course', courseId);
  }

  /**
   * Track contact form events
   */
  trackContactEvent(action: string, formType?: string): void {
    this.trackEvent(action, 'Contact', formType);
  }

  /**
   * Check if analytics is ready and consent is given
   */
  isReady(): boolean {
    return this.isInitialized && this.canTrack('analytics');
  }

  /**
   * Get current consent preferences
   */
  getConsentPreferences(): ConsentPreferences | null {
    return this.consentPreferences;
  }

  /**
   * Check if tracking is allowed for a specific type
   */
  private canTrack(type: 'analytics' | 'marketing' | 'functional'): boolean {
    if (!this.config.respectConsent) {
      return true; // Always allow if consent checking is disabled
    }

    if (!this.consentPreferences) {
      return false; // No consent preferences loaded
    }

    return this.consentPreferences[type];
  }

  /**
   * Queue events for later processing when consent is given
   */
  private queueEvent(eventFn: () => void): void {
    this.pendingEvents.push(eventFn);
    
    // Limit queue size to prevent memory issues
    if (this.pendingEvents.length > 100) {
      this.pendingEvents = this.pendingEvents.slice(-50);
    }
  }

  /**
   * Process queued events when consent is granted
   */
  private processPendingEvents(): void {
    if (this.pendingEvents.length > 0) {
      console.log(`Processing ${this.pendingEvents.length} pending analytics events`);
      
      this.pendingEvents.forEach(eventFn => {
        try {
          eventFn();
        } catch (error) {
          console.error('Error processing pending event:', error);
        }
      });
      
      this.pendingEvents = [];
    }
  }

  /**
   * Load consent preferences from localStorage or cookies
   */
  private async loadConsentPreferences(): Promise<ConsentPreferences> {
    try {
      // Try to load from localStorage first
      const stored = localStorage.getItem('cookie-consent-preferences');
      if (stored) {
        const preferences = JSON.parse(stored);
        return {
          analytics: preferences.analytics || false,
          marketing: preferences.marketing || false,
          functional: preferences.functional !== false, // Default to true
        };
      }
    } catch (error) {
      console.error('Error loading consent preferences:', error);
    }

    // Default preferences (minimal consent)
    return {
      analytics: false,
      marketing: false,
      functional: true,
    };
  }

  /**
   * Store consent preferences for future sessions
   */
  private async storeConsentPreferences(preferences: ConsentPreferences): Promise<void> {
    try {
      localStorage.setItem('cookie-consent-preferences', JSON.stringify(preferences));
    } catch (error) {
      console.error('Error storing consent preferences:', error);
    }
  }
}

// Create singleton instance
export const consentAwareAnalytics = new ConsentAwareAnalyticsService({
  debug: process.env.NODE_ENV === 'development',
  testMode: process.env.NODE_ENV === 'test',
  respectConsent: true,
});

// Export the service class for testing
export { ConsentAwareAnalyticsService };

// Export measurement ID for reference
export { GA_MEASUREMENT_ID };
